import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useBranding } from "../lib/state/brandingContext";
import { tenantPath, useCatalogueMode, useTenantSlug } from "../lib/tenant";

type Step = "select" | "visualize" | "schedule";
const STEP_ORDER: Step[] = ["select", "visualize", "schedule"];

export function AppShell({ step, children }: { step: Step; children: ReactNode }) {
  const { branding } = useBranding();
  const navigate = useNavigate();
  const tenantSlug = useTenantSlug();
  const catalogueMode = useCatalogueMode();
  const currentIdx = STEP_ORDER.indexOf(step);

  // Server-authoritative gating: the demo/manufacturer scopes have no dealer, so there is no quote
  // to get — the step disappears entirely. Null branding (still loading / transient error) keeps
  // the full tenant experience.
  const leadCaptureEnabled = branding?.leadCaptureEnabled ?? true;
  const isPreview = branding != null && branding.mode !== "tenant";

  // Earlier steps are clickable so the customer can go back to edit (state persists); the current
  // step is just highlighted and later steps stay inert (forward moves happen via the page CTAs).
  const go = (target: Step) => navigate(tenantPath(tenantSlug, target === "select" ? "" : target));

  return (
    <div style={{ minHeight: "100vh" }}>
      <header style={{ background: "var(--surface)", borderBottom: "1px solid var(--border)", boxShadow: "var(--shadow-sm)" }}>
        <div className="rg-appbar">
          {branding?.logoUrl && <img src={branding.logoUrl} alt="" style={{ height: 34, objectFit: "contain" }} />}
          <strong style={{ fontSize: 17, fontWeight: 700 }}>{branding?.name ?? "Visualizer"}</strong>
          {/* Neutral badge so a demo/manufacturer catalogue is never mistaken for a dealer offer. */}
          {isPreview && (
            <span
              data-testid="catalog-preview-badge"
              style={{ padding: "3px 10px", borderRadius: "var(--radius-pill)", fontSize: 11, fontWeight: 600, background: "var(--brand-tint)", color: "var(--brand-active)", whiteSpace: "nowrap" }}
            >
              Catalogue preview
            </span>
          )}
          {/* Dealer's own showroom: the full active catalogue, browsed price-free (still their brand + quote). */}
          {catalogueMode && (
            <span
              data-testid="full-catalogue-badge"
              style={{ padding: "3px 10px", borderRadius: "var(--radius-pill)", fontSize: 11, fontWeight: 600, background: "var(--brand-tint)", color: "var(--brand-active)", whiteSpace: "nowrap" }}
            >
              Full catalogue
            </span>
          )}
          <nav className="rg-appbar-nav">
            <StepLabel label="Select" active={step === "select"} clickable={currentIdx > 0} onClick={() => go("select")} />
            <StepLabel label="Try Styles" active={step === "visualize"} clickable={currentIdx > 1} onClick={() => go("visualize")} />
            {leadCaptureEnabled && (
              <StepLabel label="Get a quote" active={step === "schedule"} clickable={false} onClick={() => go("schedule")} />
            )}
          </nav>
        </div>
      </header>
      <main style={{ maxWidth: 1100, margin: "0 auto", padding: 20 }}>{children}</main>
    </div>
  );
}

function StepLabel({ label, active, clickable, onClick }: { label: string; active: boolean; clickable: boolean; onClick: () => void }) {
  return (
    <span
      onClick={clickable ? onClick : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
      style={{
        paddingBottom: 4,
        borderBottom: `2px solid ${active ? "var(--brand)" : "transparent"}`,
        color: active ? "var(--text)" : "var(--muted)",
        fontWeight: active ? 600 : 400,
        cursor: clickable ? "pointer" : "default",
      }}
    >
      {label}
    </span>
  );
}

export const card: React.CSSProperties = {
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius)",
  padding: 16,
  boxShadow: "var(--shadow-sm)",
};

/** Deprecated inline-button style shim — prefer the <Button> component. Kept for any remaining call sites. */
export function button(accent = "var(--brand)"): React.CSSProperties {
  return {
    background: accent,
    color: "var(--on-brand)",
    border: "none",
    borderRadius: "var(--radius-pill)",
    padding: "11px 18px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  };
}
